import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { CONTACT } from "@/lib/site";

const LINES = [
  { text: "Five-star comfort,", italic: false },
  { text: "wherever", italic: true },
  { text: "you gather.", italic: false },
];

const MaskedLine = ({ text, italic, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${italic ? "font-serif italic bronze-text pr-3" : ""}`}
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-stone-50 pt-32 pb-20 lg:pt-40 lg:pb-28">
      <div className="spotlight pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-2 text-xs font-semibold tracking-[0.25em] uppercase text-brand-bronze-dark"
            data-testid="hero-overline"
          >
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
            Luxury Restroom Trailers · {CONTACT.region}
          </motion.p>
          <h1 className="mt-8 font-serif text-5xl font-light leading-[1.05] tracking-tight text-stone-950 sm:text-6xl lg:text-7xl" data-testid="hero-heading">
            {LINES.map((l, i) => (
              <MaskedLine key={l.text} text={l.text} italic={l.italic} delay={0.25 + i * 0.14} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-stone-600 md:text-lg"
            data-testid="hero-subtext"
          >
            LooXury Rentals delivers beautifully appointed restroom trailers for weddings, corporate
            events, festivals, private parties, construction projects, and film &amp; TV production —
            anywhere in Simcoe County and beyond.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-3 bg-stone-900 px-8 py-4 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900"
            >
              Book Your Trailer
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <a
              href="#trailers"
              data-testid="hero-explore-link"
              className="link-underline px-2 py-4 text-sm tracking-wide text-stone-900"
            >
              Explore the fleet
            </a>
          </motion.div>
        </motion.div>
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: imgY }}
            className="gallery-frame"
          >
            <div className="relative overflow-hidden">
              <img
                src="/images/hero-trailer.png"
                alt="LooXury luxury restroom trailer at a golden-hour estate wedding"
                className="aspect-[4/5] w-full object-cover object-left"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="absolute -bottom-8 -left-6 border border-brand-bronze/30 bg-stone-50/90 px-6 py-5 backdrop-blur-xl sm:-left-10"
            data-testid="hero-badge"
          >
            <p className="font-serif text-3xl font-light text-stone-900">White-glove</p>
            <p className="mt-1 text-xs font-semibold tracking-[0.2em] uppercase text-brand-bronze-dark">
              Delivery · Setup · Pickup
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
